<template>
    <v-card class="pa-0" style="min-height: 400px; border-left: 4px solid var(--v-primary-base);">
        <v-card-title class="font-weight-bold py-2">
            <v-icon left color="primary" class="mr-1">mdi-home-account</v-icon>
            <span style="font-size: 15px;">WFH eligibility</span>
            <v-spacer></v-spacer>
            <v-chip v-if="data" x-small label :color="data.eligible ? 'success' : 'red'" text-color="white" class="px-2">
                {{ data.eligible ? 'Eligible' : 'Not eligible' }}
            </v-chip>
            <v-icon v-if="!loading" small class="ml-2" @click="getData">mdi-refresh</v-icon>
        </v-card-title>
        <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>
        <v-divider class="my-0"></v-divider>

        <div v-if="error" class="caption grey--text pa-4 text-center">Couldn't load WFH eligibility.</div>
        <template v-else-if="data">
            <div class="d-flex align-center px-1 pt-2">
                <v-btn icon small :disabled="!data.prev_week || loading" title="Previous week" @click="goTo(data.prev_week)">
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <div class="caption grey--text text-center flex-grow-1">
                    Week {{ fmtDay(data.week.from) }} – {{ fmtDay(data.week.to) }},
                    based on {{ fmtDay(data.judged_week.from) }} – {{ fmtDay(data.judged_week.to) }}
                    <span v-if="!data.judged_complete">(so far)</span>
                    <div v-if="!data.is_current">
                        <a class="primary--text" @click="goTo(null)">Back to this week</a>
                    </div>
                </div>
                <v-btn icon small :disabled="!data.next_week || loading" title="Next week" @click="goTo(data.next_week)">
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
            </div>
            <v-list dense class="py-0">
                <v-list-item v-for="rule in data.rules" :key="rule.key" style="min-height: 0;">
                    <v-list-item-icon class="mr-3 my-1">
                        <v-icon small :color="rule.passed ? 'green darken-2' : 'red'">
                            {{ rule.passed ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="py-1">
                        <v-list-item-title style="white-space: normal;">{{ label(rule) }}</v-list-item-title>
                        <v-list-item-subtitle v-if="!rule.passed" class="red--text" style="white-space: normal;">
                            {{ detail(rule) }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </template>
    </v-card>
</template>

<script>
// The signed-in staff member's WFH eligibility. Every figure and pass/fail
// comes from the backend (WfhEligibilityService); this only formats. The
// backend also bounds the weeks that can be browsed (prev_week / next_week).
export default {
    name: 'WfhEligibilityCard',
    data: () => ({
        data: null,
        week: null, // Sunday being viewed; null = the current week
        loading: false,
        error: false,
    }),
    mounted() {
        this.getData()
    },
    methods: {
        goTo(week) {
            this.week = week
            this.getData()
        },
        getData() {
            this.loading = true
            this.error = false
            const params = this.week ? { week: this.week } : {}
            this.$axios.get('/api/dashboard/wfh-eligibility', { params, silentError: true }).then(resp => {
                this.data = resp.data
            }).catch(() => {
                this.error = true
            }).finally(() => this.loading = false)
        },
        label(rule) {
            switch (rule.key) {
                case 'no_leave':      return 'No leave in the past week'
                case 'on_time':       return 'On time every day in the past week'
                case 'min_hours':     return `Completed ${this.fmtHours(rule.required_minutes)} working hours each day`
                case 'no_holiday':    return 'No public holiday in the week'
                case 'service_year':  return `Completed ${this.fmtService(rule.months)} of service`
                case 'position':      return 'Position eligible for WFH'
                case 'wfh_open':      return 'WFH open for the week'
            }
            return rule.key
        },
        detail(rule) {
            switch (rule.key) {
                case 'no_leave':
                    return rule.days.map(d => `${d.type} ${this.fmtDate(d.date)}`).join(', ')
                case 'on_time':
                    return rule.days.map(d => d.absent
                        ? `absent ${this.fmtDate(d.date)}`
                        : `late ${this.fmtDate(d.date)} (${d.minutes} min)`).join(', ')
                case 'min_hours':
                    if (rule.no_worked_day) return 'No working day in the past week'
                    return rule.days.map(d => d.absent
                        ? `absent ${this.fmtDate(d.date)}`
                        : `${this.fmtDate(d.date)}: ${this.fmtHours(d.worked)}`).join(', ')
                case 'no_holiday':
                    return rule.holidays.map(h => `${h.name} (${this.fmtDate(h.date)})`).join(', ')
                case 'service_year':
                    if (!rule.joined) return 'Joining date not recorded'
                    return `Joined ${this.fmtFullDate(rule.joined)}, eligible from ${this.fmtFullDate(rule.eligible_from)}`
                case 'position':
                    return rule.passed ? '' : `${rule.position} isn't eligible for WFH`
                case 'wfh_open':
                    return "WFH isn't open for this week"
            }
            return ''
        },
        fmtService(months) {
            if (!months) return ''
            if (months % 12 === 0) return months === 12 ? 'one year' : `${months / 12} years`
            return `${months} months`
        },
        fmtDate(ymd) {
            const [, m, d] = ymd.split('-')
            return `${d}/${m}`
        },
        fmtFullDate(ymd) {
            const [y, m, d] = ymd.split('-')
            return `${d}/${m}/${y}`
        },
        fmtDay(ymd) {
            const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${ymd}T00:00:00`).getDay()]
            return `${day} ${this.fmtDate(ymd)}`
        },
        fmtHours(mins) {
            return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`
        },
    },
}
</script>
